/* eslint-disable no-console */
import { useState, useEffect, useRef, useCallback } from "react";

const DEFAULT_INTERVAL = 30000; // 30s
const DEFAULT_TIMEOUT = 15000; // 15s
const DEFAULT_RETRY_DELAY = 5000; // 5s
const DEFAULT_RETRY_ATTEMPTS = 2;
const RECONNECT_DELAY = 2000;

/**
 * Clean, simplified network-status hook.
 * - Simple manual timeout via Promise.race
 * - Retries only when transitioning online -> offline
 * - No retries while already offline
 * - No overlapping requests
 * - Pauses polling when tab hidden
 */
const useNetworkStatus = (options = {}) => {
  const {
    url = "/online.json",
    interval = DEFAULT_INTERVAL,
    timeout = DEFAULT_TIMEOUT,
    maxRetries = DEFAULT_RETRY_ATTEMPTS,
    retryDelay = DEFAULT_RETRY_DELAY,
    onStatusChange,
  } = options;

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isOnlineRef = useRef(isOnline);

  const checkingRef = useRef(false);
  const retryRef = useRef(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    isOnlineRef.current = isOnline;
  }, [isOnline]);

  const fetchWithTimeout = (url, timeoutMs) => {
    // This doesn't cancel the fetch, just ignores the result if it times out
    return Promise.race([
      fetch(url, { method: "HEAD", cache: "no-store", priority: "high" }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), timeoutMs)
      ),
    ]);
  };

  const runHeartbeat = useCallback(
    async (trigger, retryCount = 0, requestId = null) => {
      const id = requestId || Math.random().toString(36).slice(2, 8);

      // Prevent overlapping heartbeats — EXCEPT for retries
      // Retry attempts must be allowed even if a prior request is still pending
      if (checkingRef.current && retryCount === 0) {
        console.log(
          `Heartbeat already running, skipping non-retry heartbeat... (trigger: ${trigger}, id: ${id})`
        );
        return;
      }

      checkingRef.current = true;

      try {
        const retrySuffix = retryCount ? `&retry=${retryCount}` : "";
        const heartbeatUrl = `${url}?trigger=${trigger}&id=${id}${retrySuffix}`;

        const response = await fetchWithTimeout(heartbeatUrl, timeout);
        const newStatus = response.ok;

        retryRef.current = 0;

        setIsOnline(prev => {
          if (prev !== newStatus && onStatusChange) onStatusChange(newStatus);
          return newStatus;
        });
      } catch (err) {
        console.log(
          `Heartbeat error: ${err.message}${
            retryCount > 0 ? ` (retry attempt ${retryCount}/${maxRetries})` : ""
          }`
        );

        // If already offline, do nothing
        if (!isOnlineRef.current) {
          console.log("Already offline — no retries while offline");
          return;
        }

        // Retry only when transitioning online -> offline
        if (isOnlineRef.current && retryCount < maxRetries) {
          const next = retryCount + 1;
          retryRef.current = next;
          checkingRef.current = false;

          setTimeout(() => runHeartbeat(trigger, next, id), retryDelay);
          return;
        }

        // Max retries reached, set offline
        retryRef.current = 0;
        setIsOnline(prev => {
          if (prev !== false && onStatusChange) onStatusChange(false);
          return false;
        });
      } finally {
        checkingRef.current = false;
      }
    },
    [url, timeout, retryDelay, maxRetries, onStatusChange]
  );

  useEffect(() => {
    const handleOnline = () => {
      setTimeout(() => runHeartbeat("online"), RECONNECT_DELAY);
    };

    const handleOffline = () => {
      runHeartbeat("offline");
    };

    // Pause heartbeats when tab is hidden and esume when visible
    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        runHeartbeat("visible");
        intervalRef.current = setInterval(
          () => runHeartbeat("interval"),
          interval
        );
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", handleVisibility);

    runHeartbeat("initial");

    intervalRef.current = setInterval(() => runHeartbeat("interval"), interval);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", handleVisibility);

      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { isOnline };
};

export default useNetworkStatus;
